import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { format } from 'date-fns';

import { LATON } from '#components/mobile/ayuda/OjoBuey';
import { Gavi } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, radio, texto } from '#components/mobile/ui/tokens';
import { useLanguage } from '#hooks/useLocale';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';
import { usePlan } from '#hooks/usePlan';
import { registrarUso } from '#uso';

import {
  alPedirHoja,
  marcarVistaHoy,
  ponerSoloVer,
  vistaHoy,
} from './cerrojoPlan';

/**
 * Rumbo: la puerta de planes en la app (Vadik, 30-oct-2026). Se monta una vez.
 * Si la cuenta está en «solo ver» (periodo terminado, ver usePlan):
 *   - la hoja «Tu periodo ha terminado» sale una vez al día al abrir,
 *   - una pastilla arriba («Solo ver») la vuelve a abrir,
 *   - las acciones que escriben llaman a `comprobarPlan()` y la abren.
 */
export function PuertaPlan() {
  const { t } = useTranslation();
  const { plan, soloVer } = usePlan();
  const [abierta, setAbierta] = useState(false);

  useEffect(() => {
    ponerSoloVer(soloVer);
    if (soloVer && !vistaHoy()) {
      marcarVistaHoy();
      setAbierta(true);
      registrarUso('plan_periodo_terminado', { origen: 'abrir' });
    }
    return () => ponerSoloVer(false);
  }, [soloVer]);

  useEffect(() => alPedirHoja(() => setAbierta(true)), []);

  if (!soloVer) {
    return null;
  }

  return (
    <>
      <Button
        variant="bare"
        aria-label={t('View only. Your period has ended')}
        data-testid="pastilla-solo-ver"
        onPress={() => setAbierta(true)}
        style={{
          position: 'fixed',
          top: 'calc(6px + env(safe-area-inset-top, 0px))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 150,
          minHeight: 30,
          padding: '0 12px',
          borderRadius: radio.pildora,
          backgroundColor: color.warnSoft,
          color: color.fg,
          fontSize: 12.5,
          fontWeight: 800,
          boxShadow: '0 2px 8px rgba(14,20,36,.15)',
        }}
      >
        <Trans>View only · Keep going ›</Trans>
      </Button>
      {abierta && plan && (
        <HojaPeriodoTerminado
          borradoEl={plan.borradoEl}
          onCerrar={() => setAbierta(false)}
        />
      )}
    </>
  );
}

export function HojaPeriodoTerminado({
  borradoEl,
  onCerrar,
}: {
  /** Segundos epoch del borrado de la cuenta del servidor, si se sabe. */
  borradoEl: number | null;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const navigate = useNavigate();
  const [budgetName] = useMetadataPref('budgetName');
  const [exportando, setExportando] = useState(false);

  const fecha =
    borradoEl != null
      ? new Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'long',
        }).format(new Date(borradoEl * 1000))
      : null;

  async function exportar() {
    setExportando(true);
    registrarUso('plan_periodo_terminado', { origen: 'exportar' });
    try {
      const respuesta = await send('export-budget');
      if (!('error' in respuesta && respuesta.error) && respuesta.data) {
        await window.Actual.saveFile(
          respuesta.data,
          `${format(new Date(), 'yyyy-MM-dd')}-${budgetName}.zip`,
          t('Export budget'),
        );
      }
    } finally {
      setExportando(false);
    }
  }

  return (
    <Hoja
      aria-label={t('Your period has ended')}
      data-testid="hoja-periodo-terminado"
      onCerrar={onCerrar}
    >
      <View style={{ gap: 12, paddingBottom: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View
            aria-hidden
            style={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              border: `3px solid ${LATON.medio}`,
              background: 'linear-gradient(180deg,#fde7c8,#cfe6f3)',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Gavi ancho={44} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 11.5,
                fontWeight: 800,
                color: LATON.oscuro,
                textTransform: 'uppercase',
                letterSpacing: '.06em',
              }}
            >
              <Trans>Your 45-day trial</Trans>
            </Text>
            <Text style={{ ...texto.titulo, color: color.fg }}>
              <Trans>Your period has ended</Trans>
            </Text>
          </View>
        </View>

        <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
          <Trans>
            Your accounts are still here, just as you left them. While you think
            it over, you can look at them and take them with you.
          </Trans>
        </Text>

        <View style={{ gap: 6 }}>
          <Linea si>
            <Trans>See your accounts, transactions and reports</Trans>
          </Linea>
          <Linea si>
            <Trans>Export everything to a file</Trans>
          </Linea>
          <Linea>
            <Trans>Add new transactions</Trans>
          </Linea>
          <Linea>
            <Trans>Save to the cloud and share with your partner</Trans>
          </Linea>
        </View>

        {fecha && (
          <Text
            style={{
              ...texto.secundario,
              color: color.fg2,
              backgroundColor: color.bg,
              borderRadius: 12,
              padding: '10px 12px',
            }}
          >
            {t(
              'If you do not keep going, on {{date}} I delete your account from the server to make room. I will let you know 7 days before and the day before.',
              { date: fecha },
            )}
          </Text>
        )}

        <Text
          style={{ ...texto.secundario, color: color.fg2, fontStyle: 'italic' }}
        >
          {t(
            'I live for free at the port, but the server does not: it asks for euros every month. It does not even take sardines.',
          )}
        </Text>

        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Boton
            variante="fantasma"
            style={{ flex: 1 }}
            onPress={() => {
              registrarUso('plan_periodo_terminado', { origen: 'ahora-no' });
              onCerrar();
            }}
          >
            <Trans>Not now</Trans>
          </Boton>
          <Boton
            style={{ flex: 1 }}
            onPress={() => {
              registrarUso('plan_periodo_terminado', { origen: 'seguir' });
              onCerrar();
              void navigate('/settings', { state: { abrir: 'plan' } });
            }}
          >
            <Trans>Keep going with Rumbo</Trans>
          </Boton>
        </View>
        <Boton
          variante="fantasma"
          isDisabled={exportando}
          onPress={() => void exportar()}
          style={{ backgroundColor: 'transparent', color: color.accent }}
        >
          {exportando ? t('Exporting…') : t('Export my data')}
        </Boton>
      </View>
    </Hoja>
  );
}

function Linea({
  si = false,
  children,
}: {
  si?: boolean;
  children: ReactNode;
}) {
  return (
    <View style={{ flexDirection: 'row', gap: 8, ...texto.cuerpo }}>
      <Text
        aria-hidden
        style={{
          width: 14,
          fontWeight: 800,
          color: si ? color.accent : color.fg2,
        }}
      >
        {si ? '✓' : '–'}
      </Text>
      <Text style={{ flex: 1, color: si ? color.fg : color.fg2 }}>
        {children}
      </Text>
    </View>
  );
}
